import { EventType } from "@/analytics/analytics-events";
import { SubmitHbToSxgMigrationFeedbackProperties } from "@/analytics/hb-to-sxg-migration-events-properties";
import { useSubmitExperienceFeedback } from "@/hooks/use-submit-experience-feedback";
import { createWebEditorUrlFromViewer } from "@/utils/redirects";
import { ExperienceFeedbackDialog } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback, useState, type JSX } from "react";

type SwitchToPreviousUiDialogProps = {
  /** Whether the dialog is shown */
  open: boolean;

  /** Called when the user cancels the switch and stays in the viewer */
  onClose(): void;
};

/**
 * @returns the dialog collecting the feedback of a user switching back to the HoloBuilder WebEditor,
 *  which then opens the current project there
 */
export function SwitchToPreviousUiDialog({
  open,
  onClose,
}: SwitchToPreviousUiDialogProps): JSX.Element {
  const submitExperienceFeedback = useSubmitExperienceFeedback();
  const [isSwitching, setIsSwitching] = useState(false);

  const onClosePreviousUiDialog = useCallback(() => {
    Analytics.track(EventType.cancelHbToSxgMigrationFeedback);
    onClose();
  }, [onClose]);

  const submitAndSwitch = useCallback(
    async (reasons: string[], feedback: string) => {
      Analytics.track<SubmitHbToSxgMigrationFeedbackProperties>(
        EventType.submitHbToSxgMigrationFeedback,
        { reasons, feedback },
      );
      setIsSwitching(true);

      const isSubmitted = await submitExperienceFeedback({ reasons, feedback });
      const webEditorUrl = createWebEditorUrlFromViewer(
        new URL(window.location.href),
      );

      if (isSubmitted && webEditorUrl) {
        // The spinner keeps running until the WebEditor replaces this page
        window.location.href = webEditorUrl.toString();
      } else {
        setIsSwitching(false);
      }
    },
    [submitExperienceFeedback],
  );

  return (
    <ExperienceFeedbackDialog
      open={open}
      onClose={onClosePreviousUiDialog}
      onSubmitFeedback={submitAndSwitch}
      showSpinner={isSwitching}
    />
  );
}
